import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useFormikContext } from 'formik'
import { getMembers as getPartners } from '../../../../services/membersPartnerService'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { useNearBottom } from '../../../../hooks/utils/useNearBottom'

type Partner = {
  id: number
  name: string
  image?: string
  role?: string
}

type ApiPartner = {
  id: number
  userId?: number | null
  companyName?: string
  logo?: string | null
  partnerType?: string
  description?: string
}

type Props = {
  values: number[]
  onChange: (val: number[]) => void
  placeholder?: string
  minChars?: number
  name?: string
  // Preloaded partners (already normalized to user_id) to seed the chip cache
  // when opening the modal in edit mode — otherwise chips wouldn't render until
  // the user searched for and found the exact partner in the dropdown.
  preloaded?: ApiPartner[]
}

const LIMIT = 20
const FALLBACK_LOGO = toAbsoluteUrl('/media/svg/nobilis/user_nb-gray.svg')

const normalize = (p: ApiPartner): Partner | null => {
  // AdminTask.related_partners is a M2M to AUTH_USER_MODEL — we need the
  // User PK behind the Partner, not Partner.id. Skip partners without a linked user.
  if (!p.userId) return null
  return {
    id: p.userId,
    name: p.companyName || `#${p.id}`,
    image: p.logo || undefined,
    role: p.partnerType,
  }
}

export function PartnersAutocompleteField({
  values = [],
  onChange,
  placeholder = 'Search partners…',
  minChars = 0,
  name,
  preloaded,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [items, setItems] = useState<Partner[]>([])
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedCache, setSelectedCache] = useState<Record<number, Partner>>({})
  const [rawCount, setRawCount] = useState(0)
  const formik = useFormikContext()

  const reqRef = useRef(0)
  const hasMore = items.length < count

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 300)
    return () => clearTimeout(id)
  }, [query])

  const fetchData = useCallback(async (search: string, nextOffset: number) => {
    const reqId = ++reqRef.current
    setLoading(true)
    try {
      const res: any = await getPartners({ search, limit: LIMIT, offset: nextOffset })
      if (reqId !== reqRef.current) return
      const raw: ApiPartner[] = res?.results ?? []
      const missingUser = raw.filter((p) => !p.userId).length
      if (missingUser > 0) {
        console.warn(
          `[PartnersAutocompleteField] ${missingUser}/${raw.length} partners were skipped because the API did not return userId (backend serializer may be stale — restart Django after pulling PR-I-36).`,
          raw
        )
      }
      const results = raw.map(normalize).filter(Boolean) as Partner[]
      setCount(Number(res?.count ?? results.length))
      if (nextOffset === 0) setRawCount(raw.length)
      setItems((prev) => (nextOffset === 0 ? results : [...prev, ...results]))
    } catch (err) {
      console.error('[PartnersAutocompleteField] failed to load partners', err)
      if (reqId === reqRef.current && nextOffset === 0) {
        setItems([])
        setCount(0)
      }
    } finally {
      if (reqId === reqRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (debounced.length < minChars) return
    setOffset(0)
    fetchData(debounced, 0)
  }, [debounced, minChars, fetchData])

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return
    const next = offset + LIMIT
    setOffset(next)
    fetchData(debounced, next)
  }, [loading, hasMore, offset, debounced, fetchData])

  const handleScroll = useNearBottom(loadMore)

  useEffect(() => {
    setSelectedCache((prev) => {
      const nextCache = { ...prev }
      for (const it of items) if (values.includes(it.id)) nextCache[it.id] = it
      return nextCache
    })
  }, [items, values])

  useEffect(() => {
    if (!preloaded?.length) return
    setSelectedCache((prev) => {
      const nextCache = { ...prev }
      for (const raw of preloaded) {
        const p = normalize(raw)
        if (p && !nextCache[p.id]) nextCache[p.id] = p
      }
      return nextCache
    })
  }, [preloaded])

  const handleAdd = (itemId: number) => {
    if (!values.includes(itemId)) onChange([...values, itemId])
    setQuery('')
    setOpen(false)
  }

  const handleRemove = (itemId: number) => {
    onChange(values.filter((v) => v !== itemId))
  }

  const validateForm = () => {
    if (name) {
      formik.setFieldTouched(name, true)
      formik.validateForm()
    }
  }

  const selectedObjects = values
    .map((id) => selectedCache[id])
    .filter(Boolean) as Partner[]

  return (
    <div className='nb-language-autocomplete'>
      <div className='nb-language-wrapper'>
        {selectedObjects.map((item) => (
          <div key={item.id} className='nb-admin-team-item-select'>
            <div className='tap-add-experience-2-image'>
              <img src={item.image || FALLBACK_LOGO} width={24} height={24} alt='logo' />
            </div>
            <span>
              {item.name}
              {item.role && (
                <span style={{ color: '#808080', marginLeft: 6, fontSize: 12 }}>· {item.role}</span>
              )}
            </span>
            <div onClick={() => handleRemove(item.id)}>
              <KTSVG path={toAbsoluteUrl('/media/svg/nobilis/remove.svg')} />
            </div>
          </div>
        ))}

        <input
          className='tap-add-experience-input-category nb-language-input'
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            validateForm()
          }}
          onFocus={() => {
            setOpen(true)
            validateForm()
          }}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
        />
      </div>

      {open && (
        <div className='nb-language-menu' onScroll={handleScroll}>
          {items.length === 0 && !loading && (
            <div className='empty-state'>
              {rawCount > 0
                ? 'Partners exist but are missing a linked user account.'
                : 'No partners found'}
            </div>
          )}

          {items.map((item) => (
            <div
              className='tap-add-experience-2-invitee-card cursor-pointer'
              key={item.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleAdd(item.id)}
            >
              <img
                className='tap-add-experience-2-invitee-card__avatar'
                src={item.image || FALLBACK_LOGO}
                alt='logo'
              />
              <div className='tap-add-experience-2-invitee-card__name'>{item.name}</div>
              <div className='tap-add-experience-2-invitee-card__role-wrapper'>
                <div className='tap-add-experience-2-invitee-card__role'>{item.role}</div>
              </div>
            </div>
          ))}

          {loading && (
            <div className='tap-add-experience-2-invitee-card' style={{ color: '#808080' }}>
              Loading…
            </div>
          )}
        </div>
      )}
    </div>
  )
}
